// src/components/ProtectedRoute.js
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import React, { useEffect, useState } from "react";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import { ShieldAlert, Loader2 } from "lucide-react";

const ProtectedRoute = ({ children, requiredRole = "Admin" }) => {
  const { user, loading, error, role, roleLoading, roleError, authInitialized } = useAuth();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [redirected, setRedirected] = useState(false);

  // ✅ Éviter les redirections multiples
  useEffect(() => {
    setRedirected(false);
  }, [location.pathname]);

  // VÉRIFICATION SUPPLÉMENTAIRE : Double-check avec Firestore
  useEffect(() => {
    const doubleCheckRole = async () => {
      if (!user?.uid) return;
      if (redirected) return;

      setIsValidating(true);
      setValidationError(null);

      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          setValidationError("User document not found in database");
          return;
        }

        const userData = userDoc.data();
        const actualRole = userData.userRole;

        console.log('🔐 Role verification:', {
          contextRole: role,
          firestoreRole: actualRole,
          required: requiredRole,
          match: actualRole === requiredRole
        });

        if (actualRole === requiredRole) {
          localStorage.setItem('adminUser', JSON.stringify({
            uid: user.uid,
            email: user.email,
            name: userData.name || user.email,
            role: actualRole,
            lastVerified: new Date().toISOString()
          }));
        } else {
          localStorage.removeItem('adminUser');
        }

      } catch (err) {
        console.error("Role validation error:", err);
        setValidationError("Failed to verify permissions");
      } finally {
        setIsValidating(false);
      }
    };

    if (user && !loading && !roleLoading && !redirected) {
      doubleCheckRole();
    }
  }, [user, role, requiredRole, loading, roleLoading, redirected]);

  // ── Détermine si une redirection est nécessaire (sans setState pendant le render) ──
  const shouldRedirectToLogin = !user && !redirected;
  const shouldRedirectForRole = !shouldRedirectToLogin && requiredRole && role !== requiredRole && !redirected;

  useEffect(() => {
    if (shouldRedirectToLogin) {
      console.log('❌ Pas d\'utilisateur, redirection vers login');
      setRedirected(true);
    } else if (shouldRedirectForRole) {
      console.log(`❌ Rôle incorrect: ${role} !== ${requiredRole}`);
      setRedirected(true);
    }
  }, [shouldRedirectToLogin, shouldRedirectForRole, role, requiredRole]);

  // ========== LOGS DE DÉBOGAGE ==========
  console.log('🔐 ProtectedRoute State:', {
    user: user?.email,
    role,
    requiredRole,
    loading,
    roleLoading,
    authInitialized,
    isValidating,
    hasRedirected: redirected,
    path: location.pathname
  });
  // ======================================

  // 1. Écrans de chargement
  if ((loading || roleLoading || isValidating || !authInitialized) && !redirected) {
    return (
      <div style={{ minHeight: "100vh", background: "#070d1a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans','Outfit',system-ui,sans-serif", position: "relative", overflow: "hidden" }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>

        {/* Orbe décoratif */}
        <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(245,158,11,.1) 0%, transparent 65%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", width: 48, height: 48, marginBottom: 20 }}>
          <Loader2 size={48} color="#f59e0b" strokeWidth={2} style={{ animation: "spin .9s linear infinite" }} />
        </div>
        <p style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>Vérification des permissions…</p>
        <p style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
          {!authInitialized ? "Initialisation de la session…" : "Vérification des droits admin…"}
        </p>
      </div>
    );
  }

  // 2. Erreurs
  if (error || roleError || validationError) {
    return (
      <div style={{ minHeight: "100vh", background: "#070d1a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'DM Sans','Outfit',system-ui,sans-serif" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <ShieldAlert size={30} color="#f87171" strokeWidth={1.8} />
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 8 }}>Erreur de sécurité</h2>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 22, textAlign: "center", maxWidth: 320 }}>
          {validationError || error?.message || roleError?.message}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#f59e0b,#d97706)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 0 24px rgba(245,158,11,.25)",
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  // 3. Pas connecté → Login
  if (shouldRedirectToLogin || (!user && redirected)) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // 4. Vérification du rôle
  if (shouldRedirectForRole || (requiredRole && role !== requiredRole && redirected)) {
    return <Navigate to="/login" replace state={{ from: location, error: "Accès non autorisé" }} />;
  }

  console.log('✅ Accès autorisé pour:', user?.email);
  return children;
};

export default ProtectedRoute;